import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SpinnerIcon from "../../components/global/SpinnerIcon";
import UpdatePaymentInfo from "../../components/modals/UpdatePaymentInfo";
import { getFetchCustomer } from "../../store/actions/subscribeAction";

/*
    BILLING — the saved card, and the way to change it.

    The card comes from Stripe via getFetchCustomer, so it needs a Stripe
    customer id to ask about. That id is blank for every free and comped
    account (the whole subject of backend#103), which is a real state and not
    an error: those customers have no card because they have never paid for
    anything. They get told that, rather than an empty panel or a spinner
    that never stops.
*/

// Stripe sends lowercase brand slugs. These are the four the design names;
// anything else falls through to Stripe's own string rather than being
// dropped or guessed at.
const CARD_BRANDS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "AMEX",
  american_express: "AMEX",
  discover: "Discover",
};

const brandLabel = (brand) =>
  CARD_BRANDS[String(brand || "").toLowerCase()] || brand || "Card";

const Billing = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const [cardDetails, setCardDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(false);

  const stripeCustomerId = user?.stripe_customer_id;

  useEffect(() => {
    if (!stripeCustomerId) {
      setLoading(false);
      return;
    }
    /*
        Cleared in `finally`, not in the callback. getFetchCustomer invokes
        onSuccess ONLY when the response carries `success: true` — a 401, a
        network failure or a `success: false` body all just raise a toast and
        return. Clearing the flag from the callback alone therefore left this
        panel on a spinner that never stopped, for exactly the customers whose
        card we could not read.
    */
    dispatch(
      getFetchCustomer(stripeCustomerId, (res) => setCardDetails(res))
    ).finally(() => setLoading(false));
  }, [dispatch, stripeCustomerId]);

  /*
      Tolerates being called with no event. UpdatePaymentInfo calls its
      `paymentToggle` bare after a successful card update, while the old
      Setting.js handler opened with `event.preventDefault()` — so saving a
      new card threw on `undefined.preventDefault` and the modal stayed open
      over a card that had in fact been updated.
  */
  const togglePayment = (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    setPayment((open) => !open);
  };

  return (
    <>
      <h1 className="account__heading">Payment Information</h1>

      <div className="account__panel">
        {loading ? (
          <SpinnerIcon label="Loading your payment information" />
        ) : cardDetails ? (
          <>
            <p className="account__fact account__fact--strong">
              {brandLabel(cardDetails.brand)}
            </p>
            <p className="account__fact">**** **** **** {cardDetails.last4}</p>
            <p className="account__fact mb-4">
              Expiration: {cardDetails.exp_month}/{cardDetails.exp_year}
            </p>
            <button
              type="button"
              className="gb-btn gb-btn--primary"
              onClick={togglePayment}
            >
              Update
            </button>
          </>
        ) : (
          <>
            <p className="account__fact mb-4">
              {stripeCustomerId
                ? "We don't have a payment method saved for you yet."
                : "You don't have a payment method on file. One is saved here when you subscribe to a paid API plan."}
            </p>
            {/*
                No card and no Stripe customer means there is nothing for the
                update form to attach a card to — updateCard posts
                `customerId: user.stripe_customer_id`. Offering the button
                would walk the customer into a failure.
            */}
            {stripeCustomerId && (
              <button
                type="button"
                className="gb-btn gb-btn--primary"
                onClick={togglePayment}
              >
                Update
              </button>
            )}
          </>
        )}
      </div>

      <UpdatePaymentInfo
        payment={payment}
        paymentToggle={togglePayment}
        setCardDetails={setCardDetails}
      />
    </>
  );
};

export default Billing;
